import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Tag,
  Typography,
  Switch,
  Alert,
  message,
  Popconfirm,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  listUnifiedTaskSlaDefinitions,
  createUnifiedTaskSlaDefinition,
  updateUnifiedTaskSlaDefinition,
  deleteUnifiedTaskSlaDefinition,
  type UnifiedTaskSlaDefinitionRow,
} from '@/services/unifiedTask.service';

const { Title, Paragraph, Text } = Typography;

const MODULE_OPTIONS = [
  { value: '*', label: 'Any module (*)' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Sales', label: 'Growth & Revenue' },
  { value: 'SupplyChain', label: 'Supply Chain' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Quality', label: 'Quality' },
  { value: 'Projects', label: 'Projects' },
  { value: 'HR', label: 'HR' },
];

const TASK_TYPE_OPTIONS = [
  { value: '', label: 'Any task type' },
  { value: 'Manual', label: 'Manual' },
  { value: 'Inspection', label: 'Inspection' },
  { value: 'NCR', label: 'NCR' },
  { value: 'CAPA', label: 'CAPA' },
  { value: 'Approval', label: 'Approval' },
  { value: 'Collection', label: 'Collection' },
  { value: 'Payment', label: 'Payment' },
  { value: 'Invoice', label: 'Invoice' },
  { value: 'Delivery', label: 'Delivery' },
  { value: 'Follow-up', label: 'Follow-up' },
  { value: 'Receipt', label: 'Receipt' },
  { value: 'Manufacturing', label: 'Manufacturing' },
];

const SOURCE_DOC_OPTIONS = [
  { value: '', label: 'Any source document' },
  { value: 'Manual', label: 'Manual' },
  { value: 'WorkOrder', label: 'Work order' },
  { value: 'DeliveryNote', label: 'Delivery note' },
  { value: 'ApprovalInstance', label: 'Approval' },
  { value: 'SalesOrder', label: 'Customer order' },
  { value: 'PurchaseOrder', label: 'Purchase order' },
  { value: 'SalesInvoice', label: 'Tax invoice' },
  { value: 'SupplierInvoice', label: 'Supplier invoice' },
  { value: 'PaymentVoucher', label: 'Payment voucher' },
  { value: 'JournalVoucher', label: 'Journal voucher' },
  { value: 'GoodsReceiptNote', label: 'GRN' },
  { value: 'PurchaseRequisition', label: 'PR' },
  { value: 'BidAnalysis', label: 'Bid analysis' },
  { value: 'PurchaseReturn', label: 'Purchase return' },
];

function escalationJsonFromSteps(
  steps: { level?: number; after_percent?: number; notify_user_id?: string }[] | undefined
): { level: number; after_percent: number; notify_user_id?: string }[] {
  if (!Array.isArray(steps)) return [];
  return steps
    .filter(
      (s) =>
        s != null &&
        Number.isFinite(Number(s.level)) &&
        Number.isFinite(Number(s.after_percent))
    )
    .map((s) => ({
      level: Number(s.level),
      after_percent: Number(s.after_percent),
      notify_user_id: s.notify_user_id?.trim() ? s.notify_user_id.trim().slice(0, 36) : undefined,
    }));
}

function stepsFromEscalationJson(json: unknown): { level: number; after_percent: number; notify_user_id?: string }[] {
  if (!json || !Array.isArray(json)) return [{ level: 1, after_percent: 100, notify_user_id: undefined }];
  const out = json
    .filter((x) => x && typeof x === 'object')
    .map((x: Record<string, unknown>) => ({
      level: Number(x.level),
      after_percent: Number(x.after_percent),
      notify_user_id: typeof x.notify_user_id === 'string' ? x.notify_user_id : undefined,
    }))
    .filter((s) => Number.isFinite(s.level) && Number.isFinite(s.after_percent));
  return out.length > 0 ? out : [{ level: 1, after_percent: 100, notify_user_id: undefined }];
}

export const UnifiedTaskSlaDefinitionsPage: React.FC = () => {
  const [rows, setRows] = useState<UnifiedTaskSlaDefinitionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(() => {
    setLoading(true);
    listUnifiedTaskSlaDefinitions()
      .then(setRows)
      .catch(() => {
        message.error('Could not load SLA definitions');
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      module: '*',
      task_type: '',
      source_document_type: '',
      sla_duration_minutes: 1440,
      warning_threshold_percent: 70,
      escalation_threshold_percent: 100,
      hard_breach_threshold_percent: 150,
      business_hours_only: false,
      is_active: true,
      escalation_steps: [{ level: 1, after_percent: 100, notify_user_id: '' }],
    });
    setModalOpen(true);
  };

  const openEdit = (r: UnifiedTaskSlaDefinitionRow) => {
    setEditingId(r.id);
    form.setFieldsValue({
      module: r.module,
      task_type: r.task_type ?? '',
      source_document_type: r.source_document_type ?? '',
      sla_duration_minutes: r.sla_duration_minutes,
      warning_threshold_percent: r.warning_threshold_percent,
      escalation_threshold_percent: r.escalation_threshold_percent,
      hard_breach_threshold_percent: r.hard_breach_threshold_percent,
      business_hours_only: r.business_hours_only,
      is_active: r.is_active,
      escalation_steps: stepsFromEscalationJson(r.escalation_json),
    });
    setModalOpen(true);
  };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      const escalation_json = escalationJsonFromSteps(v.escalation_steps);
      setSaving(true);
      if (editingId) {
        await updateUnifiedTaskSlaDefinition(editingId, {
          sla_duration_minutes: v.sla_duration_minutes,
          warning_threshold_percent: v.warning_threshold_percent,
          escalation_threshold_percent: v.escalation_threshold_percent,
          hard_breach_threshold_percent: v.hard_breach_threshold_percent,
          business_hours_only: v.business_hours_only,
          escalation_json,
          is_active: v.is_active,
        });
        message.success('SLA definition updated');
      } else {
        await createUnifiedTaskSlaDefinition({
          module: v.module,
          task_type: v.task_type ?? '',
          source_document_type: v.source_document_type ?? '',
          sla_duration_minutes: v.sla_duration_minutes,
          warning_threshold_percent: v.warning_threshold_percent,
          escalation_threshold_percent: v.escalation_threshold_percent,
          hard_breach_threshold_percent: v.hard_breach_threshold_percent,
          business_hours_only: v.business_hours_only,
          escalation_json,
          is_active: v.is_active,
        });
        message.success('SLA definition created');
      }
      setModalOpen(false);
      load();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string; error?: string } }; message?: string };
      const msg =
        ax?.response?.data?.message ||
        ax?.response?.data?.error ||
        ax?.message ||
        (typeof e === 'string' ? e : 'Save failed');
      message.error(typeof msg === 'string' ? msg : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteUnifiedTaskSlaDefinition(id);
      message.success('SLA definition removed');
      load();
    } catch {
      message.error('Could not remove definition');
    }
  };

  const columns: ColumnsType<UnifiedTaskSlaDefinitionRow> = [
    {
      title: 'Module',
      dataIndex: 'module',
      key: 'module',
      width: 120,
      render: (m: string) => (m === '*' ? <Tag>Any</Tag> : m),
    },
    {
      title: 'Task type',
      dataIndex: 'task_type',
      key: 'task_type',
      width: 110,
      ellipsis: true,
      render: (t: string) => t || <Text type="secondary">Any</Text>,
    },
    {
      title: 'Source doc',
      dataIndex: 'source_document_type',
      key: 'source_document_type',
      width: 130,
      ellipsis: true,
      render: (t: string) => t || <Text type="secondary">Any</Text>,
    },
    {
      title: 'Duration (min)',
      dataIndex: 'sla_duration_minutes',
      key: 'sla_duration_minutes',
      width: 110,
    },
    {
      title: 'Warn %',
      dataIndex: 'warning_threshold_percent',
      key: 'warning_threshold_percent',
      width: 72,
    },
    {
      title: 'Breach %',
      dataIndex: 'escalation_threshold_percent',
      key: 'escalation_threshold_percent',
      width: 80,
    },
    {
      title: 'Hard %',
      dataIndex: 'hard_breach_threshold_percent',
      key: 'hard_breach_threshold_percent',
      width: 72,
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (a: boolean) => (a ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_: unknown, r) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
            Edit
          </Button>
          <Popconfirm title="Remove this SLA definition?" onConfirm={() => void handleDelete(r.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Remove
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Unified task SLA</Title>
      <Paragraph type="secondary">
        Rules apply when tasks are created or synced. More specific rows (module + task type + source document) override
        generic ones. Empty task type or source document means &quot;any&quot;. Use{' '}
        <Text code>*</Text> for module to match all modules.
      </Paragraph>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Escalation scan"
        description="Schedule or call POST /unified/tasks/sla-escalation-scan so escalation_json steps can notify users when SLA elapsed percent is reached."
      />
      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add definition
          </Button>
          <Button onClick={() => load()} loading={loading}>
            Refresh
          </Button>
        </Space>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 960 }}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      </Card>

      <Modal
        title={editingId ? 'Edit SLA definition' : 'New SLA definition'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => void submit()}
        confirmLoading={saving}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="module" label="Module" rules={[{ required: true }]}>
            <Select options={MODULE_OPTIONS} disabled={!!editingId} />
          </Form.Item>
          <Form.Item name="task_type" label="Task type (empty = any)">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={TASK_TYPE_OPTIONS}
              disabled={!!editingId}
            />
          </Form.Item>
          <Form.Item name="source_document_type" label="Source document type (empty = any)">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={SOURCE_DOC_OPTIONS}
              disabled={!!editingId}
            />
          </Form.Item>
          {editingId ? (
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              Scope (module / task type / source) cannot be changed. Remove and create a new row to change matching.
            </Paragraph>
          ) : null}
          <Form.Item
            name="sla_duration_minutes"
            label="SLA duration (minutes)"
            rules={[{ required: true, type: 'number', min: 1 }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Space wrap style={{ width: '100%' }}>
            <Form.Item name="warning_threshold_percent" label="Warning %" rules={[{ required: true, type: 'number' }]}>
              <InputNumber min={0} max={500} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item
              name="escalation_threshold_percent"
              label="Breach %"
              rules={[{ required: true, type: 'number' }]}
            >
              <InputNumber min={0} max={500} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item
              name="hard_breach_threshold_percent"
              label="Hard breach %"
              rules={[{ required: true, type: 'number' }]}
            >
              <InputNumber min={0} max={500} style={{ width: 120 }} />
            </Form.Item>
          </Space>
          <Form.Item name="business_hours_only" label="Business hours only (reserved)" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Title level={5}>Escalation steps</Title>
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            When elapsed time as % of SLA duration reaches <Text code>after_percent</Text>, level runs and optional user is
            notified (POST sla-escalation-scan).
          </Paragraph>
          <Form.List name="escalation_steps">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }} wrap>
                    <Form.Item {...rest} name={[name, 'level']} label="Level" rules={[{ required: true, type: 'number' }]}>
                      <InputNumber min={1} max={99} />
                    </Form.Item>
                    <Form.Item
                      {...rest}
                      name={[name, 'after_percent']}
                      label="After %"
                      rules={[{ required: true, type: 'number' }]}
                    >
                      <InputNumber min={0} max={500} />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'notify_user_id']} label="Notify user ID">
                      <Input placeholder="UUID (optional)" style={{ width: 220 }} />
                    </Form.Item>
                    <Button type="text" danger onClick={() => remove(name)}>
                      Remove
                    </Button>
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add({ level: fields.length + 1, after_percent: 100, notify_user_id: '' })}>
                  Add step
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};
